// Utility functions for the Remote Work app
import AsyncStorage from '@react-native-async-storage/async-storage'; // Updated AsyncStorage for Expo
import axios from 'axios';

import { endpoint } from './constants';
import { Application, JobAlert, JobType, ResumeDocument } from './types';

interface State {
  data: JobType[];
  keys: string[];
  applied: string[];
  applications: Record<string, Application>;
  alerts: JobAlert[];
  onboardingCompleted: boolean;
  themeMode: 'light' | 'dark';
  language: string;
  hideAds: boolean;
  removeAdsEntitled: boolean;
  resumes: ResumeDocument[];
  // Last-write-wins clock for the scalar settings (themeMode/language) used by
  // cloud sync. Empty string = never changed locally (counts as oldest), so a
  // remote backup wins on a fresh device. See app/lib/sync/merge.ts.
  settingsUpdatedAt: string;
  // Whether the user has dismissed the first-launch "jobs are third-party
  // content" disclaimer. Per-device (not cloud-synced) — it's an informational
  // notice, not user data worth restoring on a new device.
  disclaimerAcknowledged: boolean;
}

const APPLICATION_STATUSES: Application['status'][] = [
  'applied',
  'interviewing',
  'offer',
  'rejected',
];

// Keep only valid string stage dates and default the "applied" stage to the
// record's appliedAt when it isn't set explicitly.
const cleanStageDates = (
  raw: unknown,
  appliedAt: string
): Partial<Record<Application['status'], string>> => {
  const out: Partial<Record<Application['status'], string>> = {};
  if (raw && typeof raw === 'object') {
    for (const s of APPLICATION_STATUSES) {
      const v = (raw as Record<string, unknown>)[s];
      if (typeof v === 'string' && v) out[s] = v;
    }
  }
  if (appliedAt && !out.applied) out.applied = appliedAt;
  return out;
};

// Build the application records from stored data, backfilling from the legacy
// `applied: string[]` list (older app versions only tracked membership) and
// coercing any stored records to the current shape. Pure so it can be tested.
export const normalizeApplications = (
  raw: unknown,
  appliedIds: string[]
): Record<string, Application> => {
  const out: Record<string, Application> = {};
  if (raw && typeof raw === 'object') {
    for (const [id, value] of Object.entries(raw as Record<string, Partial<Application>>)) {
      const status = APPLICATION_STATUSES.includes(value?.status as Application['status'])
        ? (value!.status as Application['status'])
        : 'applied';
      const appliedAt = typeof value?.appliedAt === 'string' ? value.appliedAt : '';
      out[id] = {
        jobId: id,
        status,
        appliedAt,
        updatedAt: typeof value?.updatedAt === 'string' ? value.updatedAt : '',
        notes: typeof value?.notes === 'string' ? value.notes : '',
        stageDates: cleanStageDates(value?.stageDates, appliedAt),
        position: typeof value?.position === 'string' ? value.position : '',
        company: typeof value?.company === 'string' ? value.company : '',
        url: typeof value?.url === 'string' ? value.url : '',
      };
    }
  }
  for (const id of appliedIds ?? []) {
    if (!out[id]) {
      out[id] = {
        jobId: id,
        status: 'applied',
        appliedAt: '',
        updatedAt: '',
        notes: '',
        stageDates: {},
        position: '',
        company: '',
        url: '',
      };
    }
  }
  return out;
};

export const getAllJobs = async () => {
  try {
    const response = await axios.get(endpoint);
    return response.data;
  } catch (error) {
    console.error('Error fetching jobs:', error);
    return [];
  }
};

export const storeState = async (state: State) => {
  try {
    await AsyncStorage.setItem('state', JSON.stringify(state));
  } catch (error) {
    console.error('Error storing state:', error);
  }
};

// Snap a font scale to the supported step (10%) and clamp it to the
// supported range (50% – 200%). Resumes saved before the wider range used
// 5% steps inside 80–140%; we don't want to leave half-step values lying
// around now that the UI only moves in 10% increments.
const normalizeFontScale = (v: number | undefined, fallback = 1) => {
  if (typeof v !== 'number' || !Number.isFinite(v)) return fallback;
  return Math.max(0.5, Math.min(2.0, Math.round(v * 10) / 10));
};

// Backfill new fields onto resumes loaded from older versions of the app so
// controlled inputs never receive `undefined`.
const normalizeResume = (r: ResumeDocument): ResumeDocument => ({
  ...r,
  personalInfo: {
    fullName: r.personalInfo?.fullName ?? '',
    title: r.personalInfo?.title ?? '',
    email: r.personalInfo?.email ?? '',
    phoneCountryCode: r.personalInfo?.phoneCountryCode ?? '',
    phone: r.personalInfo?.phone ?? '',
    location: r.personalInfo?.location ?? '',
    website: r.personalInfo?.website ?? '',
    summary: r.personalInfo?.summary ?? '',
    photo: r.personalInfo?.photo ?? '',
  },
  experience: r.experience ?? [],
  education: r.education ?? [],
  skills: r.skills ?? [],
  languages: r.languages ?? [],
  links: r.links ?? [],
  personalization: {
    colorMode: r.personalization?.colorMode ?? 'light',
    fontFamily: r.personalization?.fontFamily ?? 'sans',
    fontScale: {
      title: normalizeFontScale(r.personalization?.fontScale?.title),
      subtitle: normalizeFontScale(r.personalization?.fontScale?.subtitle),
      body: normalizeFontScale(r.personalization?.fontScale?.body),
    },
  },
});

export const getStateFromStorage = async (): Promise<State> => {
  const state: State = {
    data: [],
    keys: [],
    applied: [],
    applications: {},
    alerts: [],
    onboardingCompleted: false,
    themeMode: 'light',
    language: '',
    hideAds: false,
    removeAdsEntitled: false,
    resumes: [],
    settingsUpdatedAt: '',
    disclaimerAcknowledged: false,
  };

  try {
    const storedState = await AsyncStorage.getItem('state');

    if (storedState) {
      const parsed = JSON.parse(storedState) as Partial<State>;
      const applied = parsed.applied ?? [];
      const applications = normalizeApplications(parsed.applications, applied);
      return {
        data: parsed.data ?? [],
        keys: parsed.keys ?? [],
        // Union so every application record has a matching `applied` id and
        // vice versa (the two stay consistent across versions).
        applied: Array.from(new Set([...applied, ...Object.keys(applications)])),
        applications,
        alerts: Array.isArray(parsed.alerts) ? parsed.alerts : [],
        onboardingCompleted: parsed.onboardingCompleted ?? false,
        themeMode: parsed.themeMode === 'dark' ? 'dark' : 'light',
        language: parsed.language ?? '',
        hideAds: parsed.hideAds ?? false,
        removeAdsEntitled: parsed.removeAdsEntitled ?? false,
        resumes: Array.isArray(parsed.resumes)
          ? parsed.resumes.map(normalizeResume)
          : [],
        settingsUpdatedAt:
          typeof parsed.settingsUpdatedAt === 'string' ? parsed.settingsUpdatedAt : '',
        disclaimerAcknowledged: parsed.disclaimerAcknowledged ?? false,
      };
    }
  } catch (error) {
    console.error('Error getting state from storage:', error);
  }

  return state;
};

// The handful of named HTML entities the scraped feeds emit. Numeric and hex
// entities are decoded generically below; this map only covers names.
const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  hellip: '…',
  mdash: '—',
  ndash: '–',
  rsquo: "'",
  lsquo: "'",
  sbquo: "'",
  rdquo: '"',
  ldquo: '"',
  bdquo: '"',
  bull: '•',
  middot: '·',
  rarr: '→',
  larr: '←',
  deg: '°',
  trade: '™',
  copy: '©',
  reg: '®',
  times: '×',
  divide: '÷',
  plusmn: '±',
};

// Map a Unicode code point to a display character, dropping the invisible
// formatting code points the feeds leave behind (they render as tofu boxes or
// silently break layout). Keeps emoji and accented letters intact.
const codePointToChar = (cp: number): string => {
  if (!Number.isFinite(cp) || cp < 0 || cp > 0x10ffff) return '';
  if (cp === 160) return ' '; // nbsp → normal space
  if (cp === 8203 || cp === 8204 || cp === 8205) return ''; // ZW space / ZWNJ / ZWJ
  if (cp >= 0xfe00 && cp <= 0xfe0f) return ''; // emoji variation selectors
  // Normalise smart punctuation to ASCII so numeric and named entities agree.
  if (cp === 8216 || cp === 8217 || cp === 8218) return "'"; // curly single quotes
  if (cp === 8220 || cp === 8221 || cp === 8222) return '"'; // curly double quotes
  try {
    return String.fromCodePoint(cp);
  } catch {
    return '';
  }
};

// Some feeds double-encode their text: UTF-8 bytes get re-interpreted as
// Latin-1 or CP1252, so a "\u2013" arrives as three stray chars and "\u00f3"
// as "\u00c3\u00b3". These sequences never occur in legitimate text, so we map
// the common ones straight back to the intended character. Keys use explicit
// escapes because the bytes involved are invisible/control characters.
const MOJIBAKE: Record<string, string> = {
  // Non-breaking spaces -> normal space.
  '\u00C2\u00A0': ' ', // C2 A0 (nbsp)
  '\u00E2\u20AC\u00AF': ' ', // E2 80 AF narrow nbsp, CP1252 form
  '\u00E2\u0080\u00AF': ' ', // ...Latin-1 form
  // Punctuation, CP1252 form (byte 0x80 -> U+20AC, 0x99 -> U+2122, ...).
  '\u00E2\u20AC\u2122': "'", // right single quote
  '\u00E2\u20AC\u02DC': "'", // left single quote
  '\u00E2\u20AC\u0153': '"', // left double quote
  '\u00E2\u20AC\u009D': '"', // right double quote
  '\u00E2\u20AC\u201C': '\u2013', // en dash
  '\u00E2\u20AC\u201D': '\u2014', // em dash
  '\u00E2\u20AC\u00A6': '\u2026', // ellipsis
  '\u00E2\u20AC\u00A2': '\u2022', // bullet
  // Punctuation, Latin-1 form (byte 0x80 -> U+0080, ...).
  '\u00E2\u0080\u0099': "'",
  '\u00E2\u0080\u0098': "'",
  '\u00E2\u0080\u009C': '"',
  '\u00E2\u0080\u009D': '"',
  '\u00E2\u0080\u0093': '\u2013',
  '\u00E2\u0080\u0094': '\u2014',
  '\u00E2\u0080\u00A6': '\u2026',
  '\u00E2\u0080\u00A2': '\u2022',
  // Symbols/emoji that arrived as Latin-1 mojibake (E2 xx xx).
  '\u00E2\u0097\u008F': '\u2022', // black circle -> bullet
  '\u00E2\u0080\u0091': '-', // non-breaking hyphen
  '\u00E2\u0080\u00A8': '\n', // line separator
  '\u00E2\u0080\u00A9': '\n', // paragraph separator
  '\u00E2\u0080\u008B': '', // zero-width space
  '\u00E2\u0082\u00AC': '\u20AC', // euro sign
  '\u00E2\u0084\u00A2': '\u2122', // trademark
  '\u00E2\u009C\u00A8': '\u2728', // sparkles
  '\u00E2\u009C\u0085': '\u2705', // check mark
  '\u00E2\u00AD\u0090': '\u2B50', // star
  '\u00E2\u008F\u00B0': '\u23F0', // alarm clock
  '\u00C3\u009A': '\u00DA', // U-acute (uppercase, Latin-1 form)
  // Latin / Iberian accented letters (C3 xx) — Portuguese/Spanish listings.
  '\u00C3\u00A1': '\u00E1', // a-acute
  '\u00C3\u00A0': '\u00E0', // a-grave
  '\u00C3\u00A2': '\u00E2', // a-circumflex
  '\u00C3\u00A3': '\u00E3', // a-tilde
  '\u00C3\u00A7': '\u00E7', // c-cedilla
  '\u00C3\u00A9': '\u00E9', // e-acute
  '\u00C3\u00A8': '\u00E8', // e-grave
  '\u00C3\u00AA': '\u00EA', // e-circumflex
  '\u00C3\u00AD': '\u00ED', // i-acute
  '\u00C3\u00AC': '\u00EC', // i-grave
  '\u00C3\u00B1': '\u00F1', // n-tilde
  '\u00C3\u00B3': '\u00F3', // o-acute
  '\u00C3\u00B2': '\u00F2', // o-grave
  '\u00C3\u00B4': '\u00F4', // o-circumflex
  '\u00C3\u00B5': '\u00F5', // o-tilde
  '\u00C3\u00BA': '\u00FA', // u-acute
  '\u00C3\u00B9': '\u00F9', // u-grave
  '\u00C3\u00BC': '\u00FC', // u-umlaut
  '\u00C3\u00B6': '\u00F6', // o-umlaut
  '\u00C3\u00A4': '\u00E4', // a-umlaut
  '\u00C3\u00AB': '\u00EB', // e-umlaut
};

const MOJIBAKE_RE = new RegExp(
  Object.keys(MOJIBAKE)
    .sort((a, b) => b.length - a.length)
    .join('|'),
  'g'
);

const fixMojibake = (text: string): string =>
  text.replace(MOJIBAKE_RE, (m) => MOJIBAKE[m]);

const decodeHtmlEntities = (text: string): string =>
  text
    .replace(/&#x([0-9a-fA-F]+);/g, (_m, hex) => codePointToChar(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_m, dec) => codePointToChar(parseInt(dec, 10)))
    .replace(/&([a-zA-Z]+);/g, (m, name: string) =>
      name in NAMED_ENTITIES ? NAMED_ENTITIES[name] : m
    );

// Clean a short single-line field (job title, company, salary) for display:
// strip stray tags, repair mojibake and decode entities — but keep it on one
// line (no paragraph/bullet handling). Fixes raw "&amp;" / "Ã©" showing up in
// titles. Pure so it can be tested in isolation.
export const cleanInlineText = (raw: string | null | undefined): string => {
  if (!raw) return '';
  let text = raw.replace(/<(?:.|\n)*?>/gm, '');
  text = fixMojibake(text);
  text = decodeHtmlEntities(text);
  return text.replace(/\s+/g, ' ').trim();
};

// Turn a scraped job description into readable, line-broken plain text.
//
// The feeds are inconsistent: ~40% still carry real HTML (<p>/<ul>/<li>/<br>),
// the rest are already tag-stripped server-side into one run-on paragraph, and
// most leave undecoded entities (&#160;, &#8226;, emoji) and stray markdown
// (**bold**). We normalise all of that here so the Description modal can render
// paragraphs and bullet lists instead of an unreadable wall of text.
//
// Kept as a pure helper so it can be tested without rendering the modal.
export const stripJobHtml = (raw: string | null | undefined): string => {
  if (!raw) return '';

  let text = raw
    // Structural HTML → line breaks BEFORE stripping, so block boundaries
    // survive as newlines instead of collapsing words together.
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\s*li[^>]*>/gi, '\n• ')
    .replace(
      /<\s*\/\s*(?:p|div|section|article|header|footer|ul|ol|li|h[1-6]|tr|table|blockquote|pre)\s*>/gi,
      '\n'
    )
    .replace(/<\s*(?:p|div|section|article|ul|ol|h[1-6]|tr|blockquote)[^>]*>/gi, '\n')
    // Strip whatever inline tags are left (<strong>, <a>, <span>, …).
    .replace(/<(?:.|\n)*?>/gm, '');

  text = fixMojibake(text);
  text = decodeHtmlEntities(text);

  text = text
    // Drop leftover markdown emphasis markers, keeping the wrapped text.
    .replace(/\*\*([\s\S]*?)\*\*/g, '$1')
    .replace(/\*\*/g, '')
    // Split run-on jams where a clause end is glued to the next sentence:
    // "decisions.Key" / "summary:We" / "(as applicable)Employment".
    .replace(/([a-z0-9])([.:;])(?=[A-ZÀ-Þ])/g, '$1$2\n')
    .replace(/([a-z0-9])\)(?=[A-ZÀ-Þ])/g, '$1)\n')
    // Each bullet on its own line.
    .replace(/[ \t]*•[ \t]*/g, '\n• ')
    // Tidy whitespace: collapse spaces, trim around newlines, collapse the
    // doubled breaks that adjacent block tags produce into single newlines.
    .replace(/[ \t]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim();

  return text;
};

export const getTags = (jobs: JobType[]) => {
  const tags: Record<string, number> = {};
  const topTags: string[] = [];
  
  jobs.forEach((job: JobType) =>
    job.tags?.forEach((tag) => tags[tag] = tags[tag] + 1 || 1)
  );
  
  for (const tag in tags) {
    if (tags[tag] > 15) {
      topTags.push(tag);
    }
  }
  
  return topTags;
};

// Add dummy default export for Expo Router
export default {
  getAllJobs,
  storeState,
  getStateFromStorage,
  getTags,
  stripJobHtml,
  cleanInlineText,
  normalizeApplications,
};
